import { ChangeEvent, useCallback, useMemo, useState, useEffect } from "react";
import { Box, Input as MuiInput, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { ReactComponent as SearchIcon } from "../../assets/svg/Search.svg";
import style from "./nurse.module.css";
import { Button, LabelBox } from "../../components";
import { colors } from "../../theme/colors";
import { RootState, useAppSelector } from "../../store";
import { CustomTable, StatusBadge } from "../../components/DataTable";
import { ReactComponent as DeactivateIcon } from "../../assets/svg/deactivate.svg";
import { ReactComponent as EditIcon } from "../../assets/svg/edit.svg";
import { ReactComponent as EyeIcon } from "../../assets/svg/eye.svg";
import { CustomActionMenu } from "../../components/Menu";
import type { ActionItem } from "../../components/Menu";
import Logs from "../../assets/images/logs.png";
import {
  useGetUsersListQuery,
  useGetUserRolesQuery,
  useUpdateUserMutation,
} from "../../services/api";
import {
  ConfirmationDialog,
  DetailsDialog,
  InputDialog,
} from "../../components/Dialog";
import { Input } from "../../components/UserInput";
import { updateStatusSchema } from "../../yup";
import { getErrorMessage } from "../../utils/common";
import { DEFAULT_NURSE_ROLE_ID } from "../../utils/constants";

type FormState = typeof updateStatusSchema.__outputType;
type NurseAction = "view" | "edit" | "logs" | "deactivate" | "activate";

/**
 * Lists nurses for the current health org with search, view, edit, and status toggle actions.
 *
 * Returns:
 *   JSX.Element React component rendering the Nurse table and related dialogs.
 *
 * Notes:
 *   Filters users by the "Nurse" role id when available; status changes trigger
 *   a refetch of the user list to keep the table in sync.
 */
// Functional component representing the nurses page
const Nurses = () => {
  const { userDetails } = useAppSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const [updateUser] = useUpdateUserMutation();

  const healthOrgId = userDetails?.health_org_id ?? 0;

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [selectedNurse, setSelectedNurse] = useState<Users | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [reasonModalVisible, setReasonModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [logModalVisible, setLogModalVisible] = useState(false);

  const { data: rolesData } = useGetUserRolesQuery({
    is_active: true,
    is_detailed: true,
  });


  const nurseRoleId = useMemo(
    () =>
      rolesData?.items?.find((role: { name: string }) => role?.name === "Nurse")
        ?.id,
    [rolesData]
  );

  // all query parameters and pagination params
  const [queryParam, setQueryParam] = useState({
    search_key: "",
    order_by_columns: "surname",
    order: "Asc",
    is_detailed: true,
    health_org_id: healthOrgId,
    user_role_id: nurseRoleId ?? DEFAULT_NURSE_ROLE_ID,
  });

  const {
    data: nursesData,
    refetch: refetchUsers,
    isFetching,
  } = useGetUsersListQuery(queryParam, {
    skip: !healthOrgId,
  });

  // Keep health_org_id in sync once userDetails load (e.g., after refresh).
  useEffect(() => {
    if (!healthOrgId) return;
    setQueryParam((prev) => ({
      ...prev,
      health_org_id: healthOrgId,
    }));
  }, [healthOrgId]);

  const hasNurseRole = Boolean(nurseRoleId);

  const handleMenu = (value: NurseAction, nurse: Users) => {
    setSelectedNurse(nurse);
    switch (value) {
      case "deactivate":
      case "activate":
        setModalVisible(true);
        break;
      case "view":
        navigate(`/nurses/details/${nurse?.id}`, {
          state: { userRole: nurseRoleId },
        });
        break;
      case "edit":
        navigate(`/nurses/edit/${nurse?.id}`);
        break;
      case "logs":
        setLogModalVisible(true);
        break;
      default:
        break;
    }
  };

  const fields = [
    {
      id: "surname",
      label: "Name",
      flex: 2.5,
      sortable: true,
      type: "name",
      render: (item: Users) => (
        <Typography
          className={style.cell}
          sx={{
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "2",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {`${item.surname?.toUpperCase() ?? ""}, ${item.given_name ?? ""} ${item.prefix ?? ""
            }`}
        </Typography>
      ),
    },
    {
      id: "mail",
      label: "Email",
      flex: 2.5,
      render: (item: Users) => (
        <Typography
          className={style.cell}
          sx={{
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "2",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {`${item.mail ?? ""}`}
        </Typography>
      ),
    },
    {
      id: "is_active",
      label: "Status",
      type: "badge",
      flex: 1.5,
      sortable: true,
      render: (item: Users) => (
        <StatusBadge isActive={item.is_active ?? false} />
      ),
    },
    {
      id: "logo_s3_key_name",
      label: "Actions",
      type: "action",
      render: (item: Users) => (
        <CustomActionMenu<Users, NurseAction>
          item={item}
          isActive={item?.is_active ?? false}
          handleChange={handleMenu}
          actions={
            [
              {
                label: "View",
                value: "view",
                icon: <EyeIcon height={24} width={24} />,
              },
              ...(item.is_active
                ? ([
                  {
                    label: "Edit",
                    value: "edit",
                    icon: <EditIcon height={24} width={24} />,
                  },
                ] as ActionItem<NurseAction>[])
                : []),
              {
                label: "Logs",
                value: "logs",
                icon: <img src={Logs} alt="Logs" width={24} />,
              },
              {
                label: item.is_active ? "Deactivate" : "Activate",
                value: item.is_active ? "deactivate" : "activate",
                icon: item.is_active ? (
                  <DeactivateIcon height={24} width={24} />
                ) : (
                  <PowerSettingsNewIcon height={24} width={24} />
                ),
              },
            ] as ActionItem<NurseAction>[]
          }
        />
      ),
    },
  ];

  // for searching in the query
  const handleSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setQueryParam((prevParams) => ({
      ...prevParams,
      search_key: value,
    }));
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm<FormState>({
    resolver: yupResolver(updateStatusSchema),
  });

  const onSubmit = async (values: FormState) => {
    if (!selectedNurse?.id) {
      setMessage({
        type: "error",
        text: "No nurse selected for status update.",
      });
      return;
    }
    try {
      setIsLoading(true);
      const { deactivate_reason } = values;
      await updateUser({
        id: selectedNurse.id,
        is_active: !selectedNurse.is_active,
        deactivate_reason: deactivate_reason ?? "",
      }).unwrap();
      // Explicit toast and refetch keep UI feedback and table state in sync after status changes.
      setMessage({
        type: "success",
        text: "Status updated successfully.",
      });
      setReasonModalVisible(false);
      setValue("deactivate_reason", "");
      clearErrors();
      await refetchUsers();
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Something went wrong!"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Render the Nurse component
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "15px",
        }}
      >
        <MuiInput
          disableUnderline
          placeholder="Search by name...."
          type="search"
          startAdornment={<SearchIcon width={20} height={20} />}
          value={queryParam.search_key}
          onChange={handleSearch}
          sx={{ width: "550px", gap: "8px", borderRadius: "50px", backgroundColor: colors.white }}
        />
        <Button
          variant="contained"
          className={style.addButton}
          label={"Add New Nurse"}
          disabled={!hasNurseRole}
          onClick={() => {
            if (!nurseRoleId) {
              setMessage({
                type: "error",
                text: "User role is not available yet. Please wait for roles to load.",
              });
              return;
            }
            navigate("add", { state: { userRole: nurseRoleId } });
          }}
          sx={{
            ":hover": { backgroundColor: colors.primary },
          }}
        />
      </Box>
      {message && (
        <Box mt={2}>
          <LabelBox
            boxProps={{ height: 50, display: "flex", alignItems: "center" }}
            data={message}
            onClose={() => setMessage(null)}
          />
        </Box>
      )}
      <Box style={{ maxHeight: "calc(100vh - 195px)", overflow: "auto" }}>
        <CustomTable<Users>
          fields={fields}
          paginationRequired={true}
          data={nursesData?.items ?? []}
          loading={isFetching}
          onDoubleClick={(nurse: Users) => {
            navigate(`/nurses/details/${nurse?.id}`);
          }}
          defaultOrderBy={"surname"}
          searchTerm={queryParam.search_key}
          onClearSearch={() => setQueryParam((prev) => ({ ...prev, search_key: "" }))}
        />
      </Box>
      {modalVisible && (
        <ConfirmationDialog
          visible={modalVisible}
          message={`Are you sure you want to ${selectedNurse?.is_active ? "deactivate " : "activate "
            }"${selectedNurse?.surname?.toUpperCase() ?? ""}, ${selectedNurse?.given_name ?? ""
            } ${selectedNurse?.prefix ?? ""}"?`}
          onClose={() => {
            setModalVisible(false);
          }}
          onConfirm={() => {
            setModalVisible(false);
            if (selectedNurse?.is_active) {
              setReasonModalVisible(true);
            } else {
              onSubmit({ deactivate_reason: "" });
            }
          }}
        />
      )}
      {reasonModalVisible && (
        <InputDialog
          visible={reasonModalVisible}
          onClose={() => {
            setReasonModalVisible(false);
            setValue("deactivate_reason", "");
            clearErrors();
          }}
        >
          <Box
            component="form"
            sx={{ marginX: "20px" }}
            onSubmit={handleSubmit(onSubmit)}
          >
            <Typography
              variant="h2"
              component="div"
              fontSize={16}
              fontWeight={700}
              color={colors.modalHeading}
              marginBottom={"10px"}
              sx={{ display: "flex", flexDirection: "row" }}
            >
              {"Enter the reason for deactivation"}
              <Typography
                color={"red"}
                fontSize={16}
                fontWeight={700}
                sx={{ marginLeft: "5px", marginTop: "-5px" }}
              >
                {"*"}
              </Typography>
            </Typography>
            <Input
              placeholder=""
              name="deactivate_reason"
              required
              register={register}
              multiline
              rows={3}
              errors={errors}
              sx={{
                width: "100%",
                margin: "1px",
                borderWidth: "1px",
              }}
            />
            <Box className={style.horizontalButtons} sx={{ paddingX: "50px" }}>
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  backgroundColor: colors.white,
                  ":hover": { backgroundColor: colors.white },
                }}
                variant="outlined"
                onClick={() => {
                  setReasonModalVisible(false);
                  setValue("deactivate_reason", "");
                  clearErrors();
                }}
                label={"Cancel"}
              />
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  ":hover": { backgroundColor: colors.primary },
                }}
                variant="contained"
                type="submit"
                label={"Submit"}
                loading={isLoading}
              />
            </Box>
          </Box>
        </InputDialog>
      )}
      {logModalVisible && (
        <DetailsDialog
          visible={logModalVisible}
          details={selectedNurse}
          onClose={() => {
            setLogModalVisible(false);
          }}
          onConfirm={() => {
            setLogModalVisible(false);
          }}
        />
      )}
    </Box>
  );
};
export default Nurses;
